<template>
    <div class="object-groups-page">
        <header class="object-groups-header">
            <div>
                <b-button
                    variant="link"
                    class="object-groups-header__back"
                    @click="goToObjects"
                >
                    <i class="fas fa-arrow-left mr-1" aria-hidden="true"></i>
                    К строительным объектам
                </b-button>
                <h1>Группы строительных объектов</h1>
                <p>
                    Справочник ПТО для объединения объектов на одной территории или у одного
                    заказчика.
                </p>
            </div>
            <b-button
                v-if="!accessDenied"
                variant="outline-secondary"
                :disabled="loading"
                @click="loadObjectGroups"
            >
                <i
                    class="fas fa-sync-alt mr-1"
                    :class="{'fa-spin': loading}"
                    aria-hidden="true"
                ></i>
                Обновить
            </b-button>
        </header>

        <b-alert v-if="accessDenied" show variant="warning">
            <i class="fas fa-lock mr-1" aria-hidden="true"></i>
            У вас нет глобального права управления строительными объектами.
        </b-alert>

        <template v-else>
            <b-alert
                v-if="errorMessage"
                show
                dismissible
                variant="danger"
                @dismissed="errorMessage = ''"
            >
                {{ errorMessage }}
            </b-alert>

            <b-alert
                v-if="successMessage"
                show
                dismissible
                variant="success"
                @dismissed="successMessage = ''"
            >
                {{ successMessage }}
            </b-alert>

            <div v-if="loading && !hasLoaded" class="object-groups-state">
                <b-spinner small variant="primary"></b-spinner>
                <span>Загружаем группы объектов…</span>
            </div>

            <template v-else-if="hasLoaded">
                <section class="object-groups-section">
                    <div class="object-groups-section__heading">
                        <div>
                            <h2>Новая группа</h2>
                            <p>
                                Один объект может состоять только в одной группе.
                            </p>
                        </div>
                    </div>

                    <form class="object-groups-create" @submit.prevent="createGroup">
                        <b-form-input
                            v-model.trim="newGroup.name"
                            placeholder="Название группы"
                            :disabled="creating"
                        ></b-form-input>
                        <AppTextarea
                            v-model.trim="newGroup.description"
                            placeholder="Краткое описание (необязательно)"
                            :disabled="creating"
                        ></AppTextarea>
                        <b-button
                            type="submit"
                            variant="primary"
                            :disabled="creating || !newGroup.name"
                        >
                            <b-spinner v-if="creating" small class="mr-1"></b-spinner>
                            <i v-else class="fas fa-plus mr-1" aria-hidden="true"></i>
                            Создать
                        </b-button>
                    </form>
                </section>

                <section class="object-groups-section">
                    <div class="object-groups-section__heading">
                        <div>
                            <h2>Группы</h2>
                            <p>
                                Группа становится архивной автоматически, когда архивированы все
                                входящие в неё объекты.
                            </p>
                        </div>
                        <b-badge variant="light" class="object-groups-count">
                            {{ groups.length }}
                        </b-badge>
                    </div>

                    <div v-if="groups.length" class="object-groups-list">
                        <article
                            v-for="group in groups"
                            :key="group.id"
                            class="object-group-card"
                        >
                            <div class="object-group-card__fields">
                                <b-form-input
                                    v-model.trim="group.name"
                                    :disabled="isGroupSaving(group.id)"
                                    aria-label="Название группы объектов"
                                ></b-form-input>
                                <AppTextarea
                                    v-model.trim="group.description"
                                    :disabled="isGroupSaving(group.id)"
                                    placeholder="Описание"
                                    aria-label="Описание группы объектов"
                                ></AppTextarea>
                            </div>
                            <div class="object-group-card__meta">
                                <router-link :to="`/objects/groups/${group.id}`">Открыть карточку →</router-link>
                                <b-badge :variant="group.archived ? 'secondary' : 'success'">
                                    {{ group.archived ? 'Архивная' : 'Действующая' }}
                                </b-badge>
                                <span>Объектов: {{ groupObjectCount(group) }}</span>
                                <b-button
                                    type="button"
                                    size="sm"
                                    variant="outline-secondary"
                                    :disabled="isGroupSaving(group.id) || !group.name"
                                    @click="saveGroup(group)"
                                >
                                    <b-spinner
                                        v-if="isGroupSaving(group.id)"
                                        small
                                        class="mr-1"
                                    ></b-spinner>
                                    Сохранить
                                </b-button>
                            </div>
                        </article>
                    </div>
                    <b-alert v-else show variant="light" class="border mb-0">
                        Группы ещё не созданы.
                    </b-alert>
                </section>

                <section class="object-groups-section">
                    <div class="object-groups-section__heading object-groups-section__heading--objects">
                        <div>
                            <h2>Состав групп</h2>
                            <p>Перед изменением состава показываем, какие общие Telegram-группы появятся или исчезнут.</p>
                        </div>
                        <div class="object-groups-object-tools">
                            <b-form-input
                                v-model.trim="objectSearch"
                                type="search"
                                size="sm"
                                placeholder="Поиск объекта"
                            ></b-form-input>
                            <b-badge variant="light" class="object-groups-count">
                                {{ filteredObjects.length }} из {{ managedObjects.length }}
                            </b-badge>
                        </div>
                    </div>

                    <div v-if="filteredObjects.length" class="object-groups-objects">
                        <div
                            v-for="object in filteredObjects"
                            :key="object.building_id"
                            class="object-group-row"
                        >
                            <div class="object-group-row__object">
                                <router-link
                                    :to="{name: 'object-card', params: {id: object.building_id}}"
                                >
                                    {{ object.name }}
                                </router-link>
                                <small class="d-block text-muted">
                                    #{{ object.building_id }} ·
                                    {{ object.is_active ? 'Действующий объект' : 'Архивный объект' }}
                                </small>
                            </div>
                            <b-button size="sm" variant="outline-primary" @click="$refs.membership.open(object)">{{ groupOptions.find(option => option.value === object.object_group_id).text }} · Изменить</b-button>
                        </div>
                    </div>
                    <b-alert v-else show variant="light" class="border mb-0">
                        {{ managedObjects.length ? 'Поиск не дал результатов.' : 'Объекты не найдены.' }}
                    </b-alert>
                </section>
            </template>
        </template>
        <ObjectGroupMembership ref="membership" @changed="loadObjectGroups"/>
    </div>
</template>

<script>
import api from '@/utils/api';
import ObjectGroupMembership from './components/ObjectGroupMembership.vue';

function positiveInteger(value) {
    const number = Number(value);
    return Number.isInteger(number) && number > 0 ? number : null;
}

function unwrapResponse(response) {
    return response && response.data && response.data.data
        ? response.data.data
        : (response ? response.data : null);
}

export default {
    name: 'ObjectGroups',
    components: {ObjectGroupMembership},
    data() {
        return {
            groups: [],
            ungroupedObjects: [],
            loading: false,
            hasLoaded: false,
            accessDenied: false,
            creating: false,
            savingGroupId: null,
            objectSearch: '',
            errorMessage: '',
            successMessage: '',
            newGroup: {
                name: '',
                description: ''
            }
        };
    },
    computed: {
        groupOptions() {
            return [{
                value: null,
                text: 'Без группы'
            }].concat(this.groups.map(group => ({
                value: Number(group.id),
                text: group.name
            })));
        },
        managedObjects() {
            const rows = [];
            this.groups.forEach(group => {
                (Array.isArray(group.objects) ? group.objects : []).forEach(object => {
                    rows.push(Object.assign({}, object, {
                        object_group_id: Number(group.id)
                    }));
                });
            });
            this.ungroupedObjects.forEach(object => {
                rows.push(Object.assign({}, object, {
                    object_group_id: null
                }));
            });
            return rows.sort((left, right) => {
                const membershipOrder = Number(!!right.object_group_id) - Number(!!left.object_group_id);
                return membershipOrder || String(left.name || '').localeCompare(String(right.name || ''), 'ru');
            });
        },
        filteredObjects() {
            const query = String(this.objectSearch || '').trim().toLowerCase();
            if (!query) return this.managedObjects;
            return this.managedObjects.filter(object => {
                return String(object.name || '').toLowerCase().includes(query)
                    || String(object.building_id || '').includes(query);
            });
        }
    },
    mounted() {
        this.loadObjectGroups();
    },
    methods: {
        goToObjects() {
            this.$router.push({name: 'objects'});
        },
        groupObjectCount(group) {
            return Array.isArray(group && group.objects) ? group.objects.length : 0;
        },
        isGroupSaving(groupId) {
            return Number(this.savingGroupId) === Number(groupId);
        },
        async loadObjectGroups() {
            this.loading = true;
            this.errorMessage = '';
            try {
                const response = await api.get('/api/object-groups/manage', {
                    ignoreModal: true
                });
                const payload = unwrapResponse(response) || {};
                this.groups = Array.isArray(payload.groups)
                    ? payload.groups.map(group => Object.assign({}, group))
                    : [];
                this.ungroupedObjects = Array.isArray(payload.ungrouped_objects)
                    ? payload.ungrouped_objects
                    : [];
                this.accessDenied = false;
                this.hasLoaded = true;
            } catch (error) {
                if (error && error.response && error.response.status === 403) {
                    this.accessDenied = true;
                    this.hasLoaded = false;
                } else {
                    this.errorMessage = this.errorText(
                        error,
                        'Не удалось загрузить группы объектов'
                    );
                }
            } finally {
                this.loading = false;
            }
        },
        async createGroup() {
            if (this.creating || !this.newGroup.name) return;
            this.creating = true;
            this.errorMessage = '';
            this.successMessage = '';
            try {
                await api.post('/api/object-groups', {
                    name: this.newGroup.name,
                    description: this.newGroup.description || null
                }, {ignoreModal: true});
                this.newGroup = {name: '', description: ''};
                await this.loadObjectGroups();
                this.successMessage = 'Группа объектов создана.';
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось создать группу объектов'
                );
            } finally {
                this.creating = false;
            }
        },
        async saveGroup(group) {
            if (this.savingGroupId || !group || !group.name) return;
            this.savingGroupId = Number(group.id);
            this.errorMessage = '';
            this.successMessage = '';
            try {
                await api.put(`/api/object-groups/${group.id}`, {
                    name: group.name,
                    description: group.description || null
                }, {ignoreModal: true});
                await this.loadObjectGroups();
                this.successMessage = 'Группа объектов сохранена.';
            } catch (error) {
                this.errorMessage = this.errorText(
                    error,
                    'Не удалось сохранить группу объектов'
                );
            } finally {
                this.savingGroupId = null;
            }
        },
        errorText(error, fallback) {
            const data = error && error.response && error.response.data;
            if (typeof data === 'string' && data.trim()) return data;
            if (data && data.msg) return data.msg;
            return error && error.message ? error.message : fallback;
        }
    }
};
</script>

<style scoped>
.object-groups-page {
    min-height: 100%;
    padding: 1rem;
    background: #f7f9fb;
}

.object-groups-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
}

.object-groups-header__back {
    padding: 0 0 0.45rem;
}

.object-groups-header h1 {
    margin: 0;
    color: #1f2d3d;
    font-size: 1.65rem;
    font-weight: 700;
}

.object-groups-header p,
.object-groups-section__heading p {
    margin: 0.3rem 0 0;
    color: #687888;
}

.object-groups-state {
    display: flex;
    align-items: center;
    gap: 0.55rem;
    min-height: 8rem;
    color: #687888;
}

.object-groups-section {
    margin-bottom: 1rem;
    padding: 1rem;
    border: 1px solid #dfe6ed;
    border-radius: 0.65rem;
    background: #fff;
    box-shadow: 0 1px 2px rgba(31, 45, 61, 0.04);
}

.object-groups-section__heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
}

.object-groups-section__heading h2 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 700;
}

.object-groups-count {
    padding: 0.45rem 0.65rem;
    color: #566573;
    font-size: 0.82rem;
}

.object-groups-create {
    display: grid;
    grid-template-columns: minmax(180px, 0.8fr) minmax(220px, 1.2fr) auto;
    gap: 0.65rem;
}

.object-groups-list {
    display: grid;
    gap: 0.65rem;
}

.object-group-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem;
    border: 1px solid #dfe6ed;
    border-radius: 0.55rem;
    background: #f9fbfd;
}

.object-group-card__fields {
    display: grid;
    flex: 1 1 auto;
    grid-template-columns: minmax(180px, 0.7fr) minmax(220px, 1.3fr);
    gap: 0.55rem;
}

.object-group-card__meta {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    gap: 0.65rem;
    color: #687888;
    font-size: 0.88rem;
}

.object-groups-section__heading--objects {
    align-items: flex-end;
}

.object-groups-object-tools {
    display: flex;
    align-items: center;
    gap: 0.6rem;
}

.object-groups-object-tools .form-control {
    width: min(320px, 42vw);
}

.object-groups-objects {
    border-top: 1px solid #edf1f5;
}

.object-group-row {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) minmax(220px, 0.7fr);
    align-items: center;
    gap: 1rem;
    padding: 0.65rem 0;
    border-bottom: 1px solid #edf1f5;
}

.object-group-row__object a {
    color: #1769c2;
    font-weight: 600;
}

@media (max-width: 767px) {
    .object-groups-page {
        padding: 0.8rem;
    }

    .object-groups-header,
    .object-groups-section__heading,
    .object-groups-section__heading--objects,
    .object-group-card {
        align-items: stretch;
        flex-direction: column;
    }

    .object-groups-header h1 {
        font-size: 1.35rem;
    }

    .object-groups-create,
    .object-group-card__fields,
    .object-group-row {
        grid-template-columns: 1fr;
    }

    .object-group-card__meta,
    .object-groups-object-tools {
        flex-wrap: wrap;
        justify-content: space-between;
    }

    .object-groups-object-tools .form-control {
        width: 100%;
    }
}
</style>
